import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Wrench, ArrowUpDown, Lightbulb, Droplets, Shield, Sparkles, ImagePlus, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Spinner, Empty, StatusBadge, Steps, History } from "@/components/common";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { MAINT_STATUS, MAINT_FLOW, URGENCY } from "@/lib/labels";
import { MaintenancePhotos } from "@/components/Photos";

const PhotoPicker = ({ photos, setPhotos, uploading, setUploading }) => {
  const api = useApi();
  const remove = (p) => {
    setPhotos(photos.filter((x) => x.id !== p.id));
    URL.revokeObjectURL(p.preview);
    api("delete", `/owner/maintenance/photos/${p.id}`).catch(() => {});
  };
  const pick = async (e) => {
    const files = [...e.target.files].slice(0, 3 - photos.length);
    e.target.value = "";
    setUploading(true);
    const added = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name}: الحجم يتجاوز 5 ميجابايت`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api("post", "/owner/maintenance/photos", fd);
        added.push({ id: r.id, preview: URL.createObjectURL(f) });
      } catch (err) {
        toast.error(errMsg(err));
      }
    }
    setPhotos((p) => [...p, ...added]);
    setUploading(false);
  };
  return (
    <div className="space-y-2">
      <Label>صور المشكلة (حتى 3 صور، اختياري)</Label>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <div key={p.id} className="relative">
            <img src={p.preview} alt="" className="h-20 w-20 rounded-lg object-cover border border-[#E5DEC9]" data-testid={`upload-preview-${i}`} />
            <button type="button" onClick={() => remove(p)} data-testid={`upload-remove-${i}`}
              className="absolute -top-2 -left-2 rounded-full bg-red-600 p-1 text-white"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {photos.length < 3 && (
          <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-lg border-2 border-dashed border-[#E5DEC9] text-[#5C7067] hover:border-[#0F382C]" data-testid="upload-photo-label">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={uploading} onChange={pick} data-testid="upload-photo-input" />
          </label>
        )}
      </div>
    </div>
  );
};

export const ICONS = { "arrow-up-down": ArrowUpDown, lightbulb: Lightbulb, droplets: Droplets, shield: Shield, sparkles: Sparkles, wrench: Wrench };

const NewRequest = ({ open, setOpen, services, preset, onDone }) => {
  const api = useApi();
  const empty = { service_id: "", location: "", urgency: "normal", description: "", preferred_date: "" };
  const [f, setF] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setF({ ...empty, service_id: preset || "" }); setPhotos([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preset]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.service_id) return toast.error("اختر نوع الخدمة");
    setBusy(true);
    try {
      const r = await api("post", "/owner/maintenance", { ...f, photo_ids: photos.map((p) => p.id) });
      toast.success(`تم رفع البلاغ ${r.code}`);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent dir="rtl" className="max-h-[90vh] overflow-y-auto" data-testid="maintenance-dialog">
        <DialogHeader><DialogTitle className="text-right">بلاغ صيانة مشتركة</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>نوع الخدمة</Label>
            <Select dir="rtl" value={f.service_id} onValueChange={(v) => setF({ ...f, service_id: v })}>
              <SelectTrigger data-testid="maintenance-service-select"><SelectValue placeholder="اختر" /></SelectTrigger>
              <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>الموقع داخل المشروع</Label><Input required placeholder="مثال: مصعد المبنى B - الدور 3" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="maintenance-location-input" /></div>
          <div className="space-y-2">
            <Label>درجة الأولوية</Label>
            <Select dir="rtl" value={f.urgency} onValueChange={(v) => setF({ ...f, urgency: v })}>
              <SelectTrigger data-testid="maintenance-urgency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(URGENCY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>وصف المشكلة</Label><Textarea required minLength={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="maintenance-description-input" /></div>
          <div className="space-y-2"><Label>الموعد المفضل (اختياري)</Label><Input type="datetime-local" value={f.preferred_date} onChange={(e) => setF({ ...f, preferred_date: e.target.value })} data-testid="maintenance-date-input" /></div>
          <PhotoPicker photos={photos} setPhotos={setPhotos} uploading={uploading} setUploading={setUploading} />
          <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">هذه الخدمة مجانية ضمن عقد مشروعك.</p>
          <Button type="submit" disabled={busy || uploading} className="h-11 w-full rounded-full bg-[#0F382C] hover:bg-[#164E3D]" data-testid="maintenance-submit-button">
            {busy ? "جاري الإرسال..." : "إرسال البلاغ"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RequestCard = ({ r }) => (
  <div className="min-w-0 rounded-2xl border border-[#E5DEC9] bg-white p-6" data-testid={`maintenance-card-${r.code}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="text-xs text-[#5C7067] ltr-num">{r.code}</p>
        <h3 className="font-bold text-[#0F382C]">{r.service_name}</h3>
        <p className="text-sm text-[#5C7067]">{r.location} · {URGENCY[r.urgency]} · {fmtDate(r.created_at)}</p>
      </div>
      <StatusBadge map={MAINT_STATUS} status={r.status} testId={`maintenance-status-${r.code}`} />
    </div>
    <p className="mt-3 text-sm">{r.description}</p>
    <MaintenancePhotos task={r} />
    {r.status !== "rejected" && <Steps flow={MAINT_FLOW} map={MAINT_STATUS} status={r.status} testId={`maintenance-steps-${r.code}`} />}
    {r.assignee_name && <p className="text-sm text-[#5C7067]">الفني: {r.assignee_name}</p>}
    <details className="mt-3"><summary className="cursor-pointer text-sm font-semibold text-[#0F382C]">سجل الحالة</summary><div className="mt-3"><History items={r.history} map={MAINT_STATUS} /></div></details>
  </div>
);

export default function OwnerMaintenance() {
  const api = useApi();
  const [services, setServices] = useState([]);
  const [reqs, setReqs] = useState(null);
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("");
  const load = useCallback(() => {
    api("get", "/owner/maintenance").then(setReqs).catch((e) => toast.error(errMsg(e)));
  }, [api]);
  useEffect(() => {
    api("get", "/maintenance-services").then(setServices).catch(() => {});
    load();
  }, [api, load]);
  const start = (id = "") => { setPreset(id); setOpen(true); };

  return (
    <div data-testid="owner-maintenance-page">
      <PageHeader title="خدمات الملاك" subtitle="بلاغات الصيانة للمرافق المشتركة في مشروعك — مجانية ضمن العقد">
        <Button onClick={() => start()} className="rounded-full bg-[#0F382C] hover:bg-[#164E3D]" data-testid="new-maintenance-button"><Plus className="ml-1 h-4 w-4" />بلاغ جديد</Button>
      </PageHeader>
      <div className="mb-10 grid grid-cols-2 gap-4 md:grid-cols-5">
        {services.map((s) => {
          const Icon = ICONS[s.icon] || Wrench;
          return (
            <button key={s.id} onClick={() => start(s.id)} data-testid={`service-tile-${s.id}`} className="rounded-2xl border border-[#E5DEC9] bg-white p-5 text-right transition-colors hover:border-[#0F382C]">
              <Icon className="h-7 w-7 text-[#C59B27]" />
              <p className="mt-3 font-bold text-sm text-[#0F382C]">{s.name}</p>
              <p className="mt-1 text-xs text-[#5C7067]">{s.description}</p>
            </button>
          );
        })}
      </div>
      <h2 className="mb-4 text-lg font-bold text-[#0F382C]">بلاغاتي</h2>
      {reqs === null ? <Spinner /> : reqs.length === 0 ? (
        <Empty icon={Wrench} title="لا توجد بلاغات بعد" text="اختر خدمة من الأعلى لرفع بلاغ" testId="maintenance-empty" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">{reqs.map((r) => <RequestCard key={r.id} r={r} />)}</div>
      )}
      <NewRequest open={open} setOpen={setOpen} services={services} preset={preset} onDone={load} />
    </div>
  );
}
